// Intranet-side postMessage endpoint. Forwards the governance Supabase
// session AND the SDK tokens stashed in localStorage so peer Symbols
// origins (canvas, marketplace, preview) can skip per-subdomain signin.
// Whitelisted origins only; page has no UI.

import {
  getClient,
  PROJECT_GOVERNANCE,
  collectSdkTokens,
  collectSupabaseSessions
} from '../../shared/governanceBridge.js'

const ALLOWED_ORIGIN_PATTERNS = [
  /^https?:\/\/canvas\.localhost(:\d+)?$/,
  /^https?:\/\/[^.]+\.symbols\.app$/,
  /^https?:\/\/preview\.localhost(:\d+)?$/,
  /^https?:\/\/marketplace\.localhost(:\d+)?$/
]

const isAllowedOrigin = (origin) =>
  ALLOWED_ORIGIN_PATTERNS.some((re) => re.test(origin))

export const sessionBridge = {
  metadata: { title: 'Session bridge · Symbols' },
  background: '#0f1116',
  color: 'rgba(255,255,255,0.35)',
  fontFamily: 'system-ui',
  fontSize: 'Z2',
  padding: 'A',
  text: 'Session bridge. Hands off governance + SDK sessions to other Symbols subdomains.',

  onRender: (el) => {
    if (el.__bridgeInit) return
    el.__bridgeInit = true

    window.addEventListener('message', async (ev) => {
      if (!isAllowedOrigin(ev.origin)) return
      if (ev.data?.type !== 'symbols-session-request') return
      const reply = (payload) =>
        ev.source?.postMessage({ type: 'symbols-session-response', ...payload }, ev.origin)

      // Chrome's Third-Party Storage Partitioning gives this iframe an
      // empty, isolated localStorage by default. requestStorageAccess()
      // promotes us to first-party storage when Chrome's heuristics
      // approve (granted automatically when the user recently visited
      // intranet as a top-level page) so we can actually read the live
      // Supabase + SDK tokens we mean to forward.
      try {
        if (typeof document.requestStorageAccess === 'function') {
          await document.requestStorageAccess()
        }
      } catch {
        // Heuristic refused — fall through and try the partitioned
        // store. Worst case we reply `no-session` and the peer surfaces
        // its existing "sign in via the intranet first" hint.
      }

      const client = getClient(PROJECT_GOVERNANCE)
      if (!client) return reply({ ok: false, error: 'no-client' })

      let supabaseSession = null
      try {
        const { data } = await client.auth.getSession()
        supabaseSession = data?.session || null
      } catch (err) {
        return reply({ ok: false, error: err?.message || 'error' })
      }

      const sdkTokens = collectSdkTokens() || {}
      const supabaseSessions = collectSupabaseSessions() || {}
      const hasSdk = !!sdkTokens.symbols_access_token || !!sdkTokens.symbols_bridge_access_token
      const hasSb = Object.keys(supabaseSessions).length > 0
      if (!supabaseSession?.access_token && !hasSdk && !hasSb) {
        return reply({ ok: false, error: 'no-session' })
      }
      reply({
        ok: true,
        accessToken: supabaseSession?.access_token || null,
        refreshToken: supabaseSession?.refresh_token || null,
        expiresAt: supabaseSession?.expires_at || null,
        sdkTokens,
        supabaseSessions
      })
    })

    // Parent waits for this handshake before posting its request so the
    // listener is guaranteed armed.
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'symbols-session-ready' }, '*')
      }
    } catch {}
  }
}
